/**
 * Preview table for import cards with pagination, filtering, and card preview
 *
 * Supports duplicate detection display when import cards have `duplicateStatus` set.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { IoAlert, IoCheckmarkCircle, IoClose, IoCopy, IoInformationCircle, IoWarning } from 'react-icons/io5';

import CardImage from '@/components/CardImage/CardImage';
import Modal from '@/components/Modal/Modal';
import TablePagination from '@/components/TablePagination/TablePagination';
import { formatPurchasePrice } from '@/lib/price';
import type { MinimalCard } from '@/services/cardDatabase';

import type { MatchedImportCard } from '../types';
import type { ImportMode } from './ImportModeSelector';

// Re-export for backward compatibility
export type { DuplicateStatus, MatchedImportCard } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface ImportPreviewTableProps {
  cards: MatchedImportCard[];
  /** Currently selected import mode (affects row styling) */
  importMode?: ImportMode;
  /** Whether duplicate detection is active */
  hasDuplicateDetection?: boolean;
}

type FilterStatus = 'all' | 'matched' | 'name' | 'fuzzy' | 'failed' | 'new' | 'duplicate';

const PAGE_SIZE = 50;

// ============================================================================
// Component
// ============================================================================

export default function ImportPreviewTable({
  cards,
  importMode = 'skip',
  hasDuplicateDetection = false,
}: ImportPreviewTableProps): ReactElement {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredCard, setHoveredCard] = useState<MinimalCard | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [detailCard, setDetailCard] = useState<MatchedImportCard | null>(null);

  // Calculate stats
  const stats = useMemo(() => {
    const base = {
      total: cards.length,
      matched: cards.filter((c) => c.matchType === 'exact').length,
      name: cards.filter((c) => c.matchType === 'name').length,
      fuzzy: cards.filter((c) => c.matchType === 'fuzzy').length,
      failed: cards.filter((c) => c.matchType === 'none' || !c.matched).length,
    };

    if (hasDuplicateDetection) {
      return {
        ...base,
        new: cards.filter((c) => c.duplicateStatus === 'new').length,
        duplicate: cards.filter((c) => c.duplicateStatus === 'duplicate').length,
      };
    }

    return base;
  }, [cards, hasDuplicateDetection]);

  // Filter cards by status
  const filteredCards = useMemo(() => {
    switch (filterStatus) {
      case 'matched':
        return cards.filter((c) => c.matchType === 'exact');
      case 'name':
        return cards.filter((c) => c.matchType === 'name');
      case 'fuzzy':
        return cards.filter((c) => c.matchType === 'fuzzy');
      case 'failed':
        return cards.filter((c) => c.matchType === 'none' || !c.matched);
      case 'new':
        return cards.filter((c) => c.duplicateStatus === 'new');
      case 'duplicate':
        return cards.filter((c) => c.duplicateStatus === 'duplicate');
      default:
        return cards;
    }
  }, [cards, filterStatus]);

  // Pagination
  const totalPages = Math.ceil(filteredCards.length / PAGE_SIZE);
  const paginatedCards = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCards.slice(start, start + PAGE_SIZE);
  }, [filteredCards, currentPage]);

  // Reset page when filter changes
  const handleFilterChange = (status: FilterStatus) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  // Handle hover for card preview
  const handleMouseEnter = (card: MatchedImportCard, event: React.MouseEvent) => {
    if (card.matched) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverPosition({ x: rect.right + 10, y: rect.top });
      setHoveredCard(card.matched);
    }
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  // Determine if a card will be skipped based on import mode
  const willBeSkipped = (card: MatchedImportCard): boolean => {
    if (!hasDuplicateDetection) return false;
    if (importMode === 'skip' && card.duplicateStatus === 'duplicate') return true;
    if (card.matchType === 'none' || !card.matched) return true;
    return false;
  };

  return (
    <div className="space-y-4">
      {/* Stats summary - clickable filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleFilterChange('all')}
          className={clsx(
            'rounded-lg px-4 py-2 transition-all',
            filterStatus === 'all' ? 'bg-aether/20 ring-aether/50 ring-1' : 'bg-slate hover:bg-storm',
          )}
        >
          <span className="text-ash text-xs tracking-wide uppercase">Total Cards</span>
          <p className={clsx('text-lg font-semibold', filterStatus === 'all' ? 'text-pearl' : 'text-silver')}>{stats.total}</p>
        </button>

        {/* Duplicate detection stats */}
        {hasDuplicateDetection && 'new' in stats && (
          <>
            <button
              onClick={() => handleFilterChange('new')}
              className={clsx(
                'rounded-lg px-4 py-2 transition-all',
                filterStatus === 'new' ? 'bg-success/20 ring-success/50 ring-1' : 'bg-success/10 hover:bg-success/15',
              )}
            >
              <span className="text-success/70 text-xs tracking-wide uppercase">New</span>
              <p className={clsx('text-lg font-semibold', filterStatus === 'new' ? 'text-success' : 'text-success/80')}>{stats.new}</p>
            </button>

            {stats.duplicate > 0 && (
              <button
                onClick={() => handleFilterChange('duplicate')}
                className={clsx(
                  'rounded-lg px-4 py-2 transition-all',
                  filterStatus === 'duplicate' ? 'bg-gold/20 ring-gold/50 ring-1' : 'bg-gold/10 hover:bg-gold/15',
                )}
              >
                <span className="text-gold/70 text-xs tracking-wide uppercase">Duplicate</span>
                <p className={clsx('text-lg font-semibold', filterStatus === 'duplicate' ? 'text-gold' : 'text-gold/80')}>
                  {stats.duplicate}
                </p>
              </button>
            )}
          </>
        )}

        {/* Match type stats (show when no duplicate detection or as secondary) */}
        {!hasDuplicateDetection && (
          <>
            <button
              onClick={() => handleFilterChange('matched')}
              className={clsx(
                'rounded-lg px-4 py-2 transition-all',
                filterStatus === 'matched' ? 'bg-success/20 ring-success/50 ring-1' : 'bg-success/10 hover:bg-success/15',
              )}
            >
              <span className="text-success/70 text-xs tracking-wide uppercase">Exact Match</span>
              <p className={clsx('text-lg font-semibold', filterStatus === 'matched' ? 'text-success' : 'text-success/80')}>
                {stats.matched}
              </p>
            </button>

            {stats.name > 0 && (
              <button
                onClick={() => handleFilterChange('name')}
                className={clsx(
                  'rounded-lg px-4 py-2 transition-all',
                  filterStatus === 'name' ? 'bg-aether/20 ring-aether/50 ring-1' : 'bg-aether/10 hover:bg-aether/15',
                )}
              >
                <span className="text-aether/70 text-xs tracking-wide uppercase">Name Match</span>
                <p className={clsx('text-lg font-semibold', filterStatus === 'name' ? 'text-aether' : 'text-aether/80')}>{stats.name}</p>
              </button>
            )}

            {stats.fuzzy > 0 && (
              <button
                onClick={() => handleFilterChange('fuzzy')}
                className={clsx(
                  'rounded-lg px-4 py-2 transition-all',
                  filterStatus === 'fuzzy' ? 'bg-gold/20 ring-gold/50 ring-1' : 'bg-gold/10 hover:bg-gold/15',
                )}
              >
                <span className="text-gold/70 text-xs tracking-wide uppercase">Fuzzy Match</span>
                <p className={clsx('text-lg font-semibold', filterStatus === 'fuzzy' ? 'text-gold' : 'text-gold/80')}>{stats.fuzzy}</p>
              </button>
            )}
          </>
        )}

        {stats.failed > 0 && (
          <button
            onClick={() => handleFilterChange('failed')}
            className={clsx(
              'rounded-lg px-4 py-2 transition-all',
              filterStatus === 'failed' ? 'bg-danger/20 ring-danger/50 ring-1' : 'bg-danger/10 hover:bg-danger/15',
            )}
          >
            <span className="text-danger/70 text-xs tracking-wide uppercase">Not Found</span>
            <p className={clsx('text-lg font-semibold', filterStatus === 'failed' ? 'text-danger' : 'text-danger/80')}>{stats.failed}</p>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="border-storm overflow-hidden rounded-xl border">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate sticky top-0 z-10">
              <tr className="text-ash text-left text-xs tracking-wider uppercase">
                <th className="w-12 px-4 py-3">Status</th>
                <th className="px-4 py-3">Card Name</th>
                <th className="w-28 px-4 py-3">Set / #</th>
                <th className="w-16 px-4 py-3">Qty</th>
                {hasDuplicateDetection && <th className="w-20 px-4 py-3">Import</th>}
                <th className="w-16 px-4 py-3">Cond</th>
                <th className="w-20 px-4 py-3">Finish</th>
                <th className="w-28 px-4 py-3">Folder</th>
                <th className="w-10 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-storm divide-y">
              {paginatedCards.map((card, index) => {
                const isSkipped = willBeSkipped(card);
                return (
                  <tr
                    key={`${card.name}-${card.setCode}-${currentPage}-${index}`}
                    className={clsx(
                      'group transition-colors',
                      isSkipped ? 'bg-storm/30 opacity-60' : 'hover:bg-slate/50',
                      !isSkipped && card.matchType === 'none' && 'bg-danger/5',
                      !isSkipped && card.matchType === 'fuzzy' && 'bg-gold/5',
                      !isSkipped && card.matchType === 'name' && 'bg-aether/5',
                    )}
                  >
                    <td className="px-4 py-3">
                      {card.matchType === 'exact' ? (
                        <IoCheckmarkCircle className="text-success h-5 w-5" />
                      ) : card.matchType === 'name' ? (
                        <IoInformationCircle className="text-aether h-5 w-5" />
                      ) : card.matchType === 'fuzzy' ? (
                        <IoWarning className="text-gold h-5 w-5" />
                      ) : (
                        <IoAlert className="text-danger h-5 w-5" />
                      )}
                    </td>
                    <td className="px-4 py-3" onMouseEnter={(e) => handleMouseEnter(card, e)} onMouseLeave={handleMouseLeave}>
                      <span className={clsx('cursor-default font-medium', isSkipped ? 'text-ash line-through' : 'text-pearl')}>
                        {card.name}
                      </span>
                      {card.matched && card.matchType === 'name' && (
                        <span className="text-ash ml-2 text-xs">
                          → {card.matched.set.toUpperCase()} #{card.matched.collector_number}
                        </span>
                      )}
                      {card.matched && card.matchType === 'fuzzy' && <span className="text-ash ml-2 text-xs">→ {card.matched.name}</span>}
                      {card.error && <span className="text-danger ml-2 text-xs">{card.error}</span>}
                    </td>
                    <td className="text-silver px-4 py-3 whitespace-nowrap">
                      <span className="uppercase">{card.matched?.set ?? card.setCode ?? '—'}</span>
                      <span className="text-ash ml-1 font-mono text-xs">
                        #{card.matched?.collector_number ?? card.collectorNumber ?? '—'}
                      </span>
                    </td>
                    <td className="text-pearl px-4 py-3 font-medium whitespace-nowrap">
                      {importMode === 'update' && card.duplicateStatus === 'duplicate' && card.matchedCollectionCard ? (
                        <span className="text-gold">
                          {card.matchedCollectionCard.quantity} → {card.quantity}
                        </span>
                      ) : (
                        card.quantity
                      )}
                    </td>
                    {hasDuplicateDetection && (
                      <td className="px-4 py-3">
                        {card.duplicateStatus === 'new' ? (
                          <span className="bg-success/20 text-success rounded px-2 py-0.5 text-xs font-medium">New</span>
                        ) : card.duplicateStatus === 'duplicate' ? (
                          <span className="flex items-center gap-1">
                            <IoCopy className="text-gold h-3.5 w-3.5" />
                            <span className="bg-gold/20 text-gold rounded px-2 py-0.5 text-xs font-medium">
                              {importMode === 'skip' ? 'Skip' : importMode === 'update' ? 'Update' : 'Dup'}
                            </span>
                          </span>
                        ) : (
                          <span className="text-ash text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="text-silver px-4 py-3">{card.condition}</td>
                    <td className="text-silver px-4 py-3 capitalize">{card.finish}</td>
                    <td className="px-4 py-3">
                      {card.folderName ? (
                        <span className="bg-aether/20 text-aether truncate rounded px-2 py-0.5 text-xs" title={card.folderName}>
                          {card.folderName}
                        </span>
                      ) : (
                        <span className="text-ash text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setDetailCard(card)}
                        className="text-ash hover:text-pearl opacity-0 transition-all group-hover:opacity-100"
                        title="View import details"
                      >
                        <IoInformationCircle className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredCards.length}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          suffix={filterStatus !== 'all' ? '(filtered)' : undefined}
        />
      </div>

      {/* Floating card preview on hover */}
      {hoveredCard && (
        <div
          className="pointer-events-none fixed z-50"
          style={{
            left: Math.min(hoverPosition.x, window.innerWidth - 260),
            top: Math.min(hoverPosition.y, window.innerHeight - 360),
          }}
        >
          <CardImage imageUris={hoveredCard.image_uris ?? null} name={hoveredCard.name} size="normal" />
        </div>
      )}

      {/* Import data detail modal */}
      <Modal isOpen={!!detailCard} onClose={() => setDetailCard(null)} title="Import Data Details" size="md">
        {detailCard && (
          <div className="space-y-4">
            {/* Card info header */}
            <div className="flex gap-4">
              {detailCard.matched && (
                <CardImage imageUris={detailCard.matched.image_uris ?? null} name={detailCard.matched.name} size="small" />
              )}
              <div className="flex-1">
                <h3 className="text-pearl text-lg font-semibold">{detailCard.name}</h3>
                {detailCard.matched && detailCard.matchType === 'name' && (
                  <p className="text-aether mt-1 text-sm">
                    Matched by name to:{' '}
                    <span className="font-medium">
                      {detailCard.matched.set.toUpperCase()} #{detailCard.matched.collector_number}
                    </span>
                  </p>
                )}
                {detailCard.matched && detailCard.matchType === 'fuzzy' && (
                  <p className="text-gold mt-1 text-sm">
                    Fuzzy matched to: <span className="font-medium">{detailCard.matched.name}</span>
                  </p>
                )}
                {!detailCard.matched && <p className="text-danger mt-1 text-sm">No match found in database</p>}

                {/* Duplicate status badge */}
                {detailCard.duplicateStatus && (
                  <div className="mt-2">
                    {detailCard.duplicateStatus === 'new' ? (
                      <span className="bg-success/20 text-success rounded px-2 py-1 text-xs font-medium">New to collection</span>
                    ) : (
                      <span className="bg-gold/20 text-gold rounded px-2 py-1 text-xs font-medium">Already in collection</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Import source data */}
            <div className="bg-slate rounded-lg p-4">
              <h4 className="text-ash mb-3 text-xs font-semibold tracking-wide uppercase">Original Import Data</h4>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-ash">Name</dt>
                <dd className="text-pearl font-mono">{detailCard.name}</dd>

                <dt className="text-ash">Set Code</dt>
                <dd className="text-pearl font-mono uppercase">{detailCard.setCode ?? '—'}</dd>

                <dt className="text-ash">Collector #</dt>
                <dd className="text-pearl font-mono">{detailCard.collectorNumber ?? '—'}</dd>

                <dt className="text-ash">Quantity</dt>
                <dd className="text-pearl">{detailCard.quantity}</dd>

                <dt className="text-ash">Condition</dt>
                <dd className="text-pearl">{detailCard.condition}</dd>

                <dt className="text-ash">Finish</dt>
                <dd className="text-pearl capitalize">{detailCard.finish}</dd>

                <dt className="text-ash">Language</dt>
                <dd className="text-pearl uppercase">{detailCard.language}</dd>

                {detailCard.folderName && (
                  <>
                    <dt className="text-ash">Folder</dt>
                    <dd className="text-pearl">{detailCard.folderName}</dd>
                  </>
                )}

                {detailCard.purchasePrice != null && (
                  <>
                    <dt className="text-ash">Purchase Price</dt>
                    <dd className="text-pearl">{formatPurchasePrice(detailCard.purchasePrice, detailCard.purchaseCurrency ?? 'USD')}</dd>
                  </>
                )}

                {detailCard.purchaseDate && (
                  <>
                    <dt className="text-ash">Purchase Date</dt>
                    <dd className="text-pearl">{detailCard.purchaseDate}</dd>
                  </>
                )}

                {detailCard.isAltered && (
                  <>
                    <dt className="text-ash">Altered</dt>
                    <dd className="text-gold">Yes</dd>
                  </>
                )}

                {detailCard.isPlaytest && (
                  <>
                    <dt className="text-ash">Playtest</dt>
                    <dd className="text-gold">Yes</dd>
                  </>
                )}

                {detailCard.sourceId && (
                  <>
                    <dt className="text-ash">Source ID</dt>
                    <dd className="text-pearl font-mono text-xs">{detailCard.sourceId}</dd>
                  </>
                )}
              </dl>
            </div>

            {/* Matched card data (if matched) */}
            {detailCard.matched && (
              <div className="bg-slate rounded-lg p-4">
                <h4 className="text-ash mb-3 text-xs font-semibold tracking-wide uppercase">Matched Card Data</h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-ash">Scryfall ID</dt>
                  <dd className="text-pearl font-mono text-xs">{detailCard.matched.id}</dd>

                  <dt className="text-ash">Name</dt>
                  <dd className="text-pearl">{detailCard.matched.name}</dd>

                  <dt className="text-ash">Set</dt>
                  <dd className="text-pearl uppercase">{detailCard.matched.set}</dd>

                  <dt className="text-ash">Collector #</dt>
                  <dd className="text-pearl font-mono">{detailCard.matched.collector_number}</dd>
                </dl>
              </div>
            )}

            {/* Existing collection card data (if duplicate) */}
            {detailCard.matchedCollectionCard && (
              <div className="bg-gold/10 border-gold/30 rounded-lg border p-4">
                <h4 className="text-gold mb-3 text-xs font-semibold tracking-wide uppercase">Existing Collection Card</h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-ash">Quantity</dt>
                  <dd className="text-pearl">{detailCard.matchedCollectionCard.quantity}</dd>

                  <dt className="text-ash">Condition</dt>
                  <dd className="text-pearl">{detailCard.matchedCollectionCard.condition}</dd>

                  <dt className="text-ash">Finish</dt>
                  <dd className="text-pearl capitalize">{detailCard.matchedCollectionCard.finish}</dd>

                  <dt className="text-ash">Language</dt>
                  <dd className="text-pearl uppercase">{detailCard.matchedCollectionCard.language}</dd>
                </dl>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button onClick={() => setDetailCard(null)} className="text-aether hover:text-aether-glow flex items-center gap-1 text-sm">
                <IoClose className="h-4 w-4" />
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
